import { useState } from 'react';

import type { ApiS3File } from '#/api/types/ApiS3File';
import type { ApiS3FileId } from '#/api/types/ApiTypedId';
import { useI18n } from '#/i18n/hooks/useI18n';
import { HttpError } from '#/utils/HttpError';

import { useKeyedState } from './useKeyedState';

export type FileUploadStateAndActions = ReturnType<
  typeof useFileUpload
>['fileUploadStateAndActions'];

type Args = {
  fileId: ApiS3FileId | null;
  fileMeta: ApiS3File | null;
  onChange: (newFileId: ApiS3FileId | null) => void;
  onError: (error: Error) => void;
  maxSizeInMB: number | null;
  accept: string | null;
  canRemove: boolean;
  uploadFileToS3: (file: File) => Promise<ApiS3FileId>;
};

export function useFileUpload({
  fileId,
  fileMeta,
  onChange,
  onError,
  maxSizeInMB,
  accept,
  canRemove,
  uploadFileToS3,
}: Args) {
  const { i18n } = useI18n();

  // Instead of setting setIsFileUploading to false after uploading
  // just wait for the fileId to be set
  //
  // This avoid the UI flickering while state syncs
  const [isFileUploading, setIsFileUploading] = useKeyedState(false, {
    key: fileId ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const onFileSelected = async (newFile: File) => {
    if (maxSizeInMB && newFile.size > maxSizeInMB * 1000000) {
      setError(
        i18n('Maximum file size: %(maxSize)s MB', { maxSize: maxSizeInMB }),
      );
      return;
    }

    const accepted = accept
      ?.split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    const fileType = newFile.type?.toLowerCase();
    const fileExtension = newFile.name.includes('.')
      ? `.${newFile.name.split('.').pop()?.toLowerCase() ?? ''}`
      : '';

    const isAllowed =
      !accepted?.length ||
      accepted.some((value) => {
        if (fileType) {
          if (value === fileType) return true;
          if (value.endsWith('/*') && fileType.startsWith(value.slice(0, -1))) {
            return true;
          }
        }

        if (value.startsWith('.')) {
          return fileExtension === value;
        }

        return false;
      });

    if (!isAllowed) {
      if (accept) {
        setError(
          i18n('Invalid file type: Expected %(accept)s and received %(type)s', {
            accept,
            type: newFile.type,
          }),
        );
      } else {
        setError(
          i18n('Invalid file type: received %(type)s', {
            type: newFile.type,
          }),
        );
      }

      return;
    }

    setIsFileUploading(true);

    try {
      const newFileId = await uploadFileToS3(newFile);
      onChange(newFileId);
    } catch (caughtError) {
      if (
        caughtError instanceof HttpError &&
        caughtError.responseJson?.errors.some(
          (err) => err.name === 'mimeType' && err.description === 'Required',
        )
      ) {
        setError(i18n('Unsupported file format'));
      } else {
        onError(caughtError as Error);
      }

      setIsFileUploading(false);
    }
  };

  const onFileRemoved = canRemove
    ? () => {
        onChange(null);
        return;
      }
    : null;

  const fileUploadStateAndActions = {
    fileId,
    fileMeta,
    loading: Boolean(isFileUploading || (fileId && !fileMeta)),
    onFileSelected,
    onFileRemoved,
    error,
  };

  return { fileUploadStateAndActions };
}
